import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {Button, FormControl, Textarea, SkeletonBox} from '@primer/react'
import {useCallback, useRef, useState} from 'react'
import {COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER, COPILOT_INSTRUCTIONS_MAX_LENGTH} from '../utils/constants'
import {
  queryForRepos,
  useCopilotRepositoryFetching,
  type DropdownRepository,
  type SimpleRepository,
} from '../hooks/useCopilotRepoFetching'
import styles from './CopilotAssignmentForm.module.css'

export interface CopilotAssignmentFormProps {
  initialRepo?: SimpleRepository | null
  onSubmit: (content: string, selectedRepo: DropdownRepository | null, selectedBranch: string) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  textareaRef?: React.RefObject<HTMLTextAreaElement>
  textareaAriaDescribedBy?: string
}

export function CopilotAssignmentForm({
  initialRepo,
  onSubmit,
  onCancel,
  isSubmitting = false,
  textareaRef,
  textareaAriaDescribedBy,
}: CopilotAssignmentFormProps) {
  const [content, setContent] = useState('')
  const [validationError, setValidationError] = useState<string | undefined>(undefined)
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null)
  const finalTextareaRef = textareaRef || internalTextareaRef

  // Use the shared repository fetching hook
  const {
    selectedRepo,
    selectedBranch,
    setSelectedBranch,
    isLoadingBranch,
    handleRepoSelect: handleRepoSelectFromHook,
  } = useCopilotRepositoryFetching(initialRepo)

  // Wrapper to prevent selection during submission
  const handleRepoSelect = useCallback(
    (repo: DropdownRepository | undefined) => {
      if (!repo || isSubmitting) return
      handleRepoSelectFromHook(repo)
    },
    [isSubmitting, handleRepoSelectFromHook],
  )

  const handleSubmit = useCallback(async () => {
    // Clear any previous validation error
    setValidationError(undefined)

    // Validate character limit
    if (content.length > COPILOT_INSTRUCTIONS_MAX_LENGTH) {
      const errorMessage = `Instructions must be ${COPILOT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} characters or fewer. Current length: ${content.length.toLocaleString()} characters.`
      setValidationError(errorMessage)
      return
    }

    if (!selectedRepo || !selectedBranch) {
      setValidationError('Please select a repository and branch.')
      return
    }

    await onSubmit(content, selectedRepo, selectedBranch)
  }, [content, selectedRepo, selectedBranch, onSubmit])

  return (
    <div className={styles.formContainer}>
      <div className={styles.textareaContainer}>
        <FormControl disabled={isSubmitting}>
          <FormControl.Label>Optional prompt</FormControl.Label>
          <Textarea
            ref={finalTextareaRef}
            className={styles.textarea}
            placeholder={COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER}
            value={content}
            onChange={e => setContent(e.target.value)}
            rows={6}
            resize="vertical"
            aria-describedby={textareaAriaDescribedBy}
          />
          {validationError && <FormControl.Validation variant="error">{validationError}</FormControl.Validation>}
        </FormControl>
      </div>

      <div className={styles.selectorsAndButtonsContainer}>
        <div className={styles.selectorsRow}>
          <ReposSelector
            repositoryLoader={queryForRepos}
            selectionVariant="single"
            selectAllOption={false}
            onSelect={handleRepoSelect}
            currentSelection={selectedRepo || undefined}
            buttonText={selectedRepo?.nameWithOwner || 'Select repository'}
            width="medium"
            additionalButtonProps={{
              'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
              disabled: isSubmitting,
            }}
          />

          {selectedRepo && !isLoadingBranch ? (
            <RefSelector
              cacheKey="copilot-assignment-ref-selector"
              canCreate={false}
              currentCommitish={selectedBranch}
              defaultBranch={selectedRepo.defaultBranch}
              owner={selectedRepo.owner}
              repo={selectedRepo.nameOnly}
              types={['branch']}
              selectedRefType="branch"
              closeOnSelect
              hideShowAll
              disabled={isSubmitting}
              onSelectItem={ref => {
                if (!isSubmitting) {
                  setSelectedBranch(ref)
                }
              }}
            />
          ) : (
            <SkeletonBox height="32px" width="100px" />
          )}
        </div>

        <div className={styles.buttonsRow}>
          {onCancel && (
            <Button variant="invisible" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !selectedRepo || !selectedBranch}
            loading={isSubmitting}
            className={styles.assignButton}
          >
            Assign
          </Button>
        </div>
      </div>
    </div>
  )
}

try{ CopilotAssignmentForm.displayName ||= 'CopilotAssignmentForm' } catch {}